import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { documentationApi } from '../api/documentation';
import { Documentation } from '../types/documentation';
import projectService from '../services/projectService';
import { Project } from '../types/project';

const DocumentationPage: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const [documentation, setDocumentation] = useState<Documentation[]>([]);
    const [project, setProject] = useState<Project | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [newDocTitle, setNewDocTitle] = useState('');
    const [newDocDescription, setNewDocDescription] = useState('');

    useEffect(() => {
        if (projectId) {
            loadProject();
            loadDocumentation();
        }
    }, [projectId]);

    const loadProject = async () => {
        try {
            const data = await projectService.getProject(Number(projectId));
            setProject(data);
        } catch (error) {
            console.error('Error loading project:', error);
        }
    };

    const loadDocumentation = async () => {
        try {
            const response = await documentationApi.getProjectDocumentation(Number(projectId));
            setDocumentation(response.data);
        } catch (error) {
            console.error('Error loading documentation:', error);
        }
    };

    const handleCreateDocumentation = async () => {
        try {
            await documentationApi.createDocumentation(Number(projectId), {
                title: newDocTitle,
                description: newDocDescription,
            });
            setOpenDialog(false);
            setNewDocTitle('');
            setNewDocDescription('');
            loadDocumentation();
        } catch (error) {
            console.error('Error creating documentation:', error);
        }
    };

    const handleDeleteDocumentation = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this documentation?')) {
            try {
                await documentationApi.deleteDocumentation(id);
                loadDocumentation();
            } catch (error) {
                console.error('Error deleting documentation:', error);
            }
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" component="h1">
                        {project?.name || 'Project'} Documentation
                    </Typography>
                    {project?.description && (
                        <Typography color="textSecondary">
                            {project.description}
                        </Typography>
                    )}
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                >
                    Add Documentation
                </Button>
            </Box>

            <Grid container spacing={3}>
                {documentation.map((doc) => (
                    <Grid item xs={12} md={6} lg={4} key={doc.id}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" component="h2">
                                    {doc.title}
                                </Typography>
                                <Typography color="textSecondary" gutterBottom>
                                    {doc.description}
                                </Typography>
                                <Typography variant="body2">
                                    Items: {doc.items.length}
                                </Typography>
                            </CardContent>
                            <CardActions>
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={() => navigate(`/documentation/${doc.id}`)}
                                >
                                    View
                                </Button>
                                <IconButton
                                    size="small"
                                    onClick={() => navigate(`/documentation/${doc.id}/edit`)}
                                >
                                    <EditIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteDocumentation(doc.id)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {documentation.length === 0 && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                        No documentation available. Create your first documentation entry.
                    </Typography>
                </Box>
            )}

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Create New Documentation</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Title"
                        fullWidth
                        value={newDocTitle}
                        onChange={(e) => setNewDocTitle(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        label="Description"
                        fullWidth
                        multiline
                        rows={4}
                        value={newDocDescription}
                        onChange={(e) => setNewDocDescription(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleCreateDocumentation}
                        color="primary"
                        disabled={!newDocTitle}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default DocumentationPage; 